// client/src/components/member/MyTicketsPage.jsx
//
// Logged-in member portal: every ticket order tied to this member, split
// into upcoming and past. Reached at /:tenant/my-tickets after member login.
// Auth rides on the httpOnly memberAuthToken cookie (withCredentials) with a
// Bearer fallback for older localStorage-based member sessions — same
// memberHeaders() shape as PublicEventHistory.jsx.

import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';

const baseUrl = process.env.REACT_APP_BASE_URL || 'http://localhost:5000';
const MEMBER_TOKEN_KEY = 'memberToken';

function memberHeaders() {
  const token = localStorage.getItem(MEMBER_TOKEN_KEY);
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function formatDateRange(startdate, enddate) {
  if (!startdate) return '';
  try {
    const start = new Date(startdate);
    const end = enddate ? new Date(enddate) : null;
    const opts = { dateStyle: 'medium', timeStyle: 'short' };
    const s = start.toLocaleString(undefined, opts);
    return end ? `${s} – ${end.toLocaleString(undefined, opts)}` : s;
  } catch { return String(startdate); }
}

export default function MyTicketsPage() {
  const { tenant } = useParams();
  const navigate = useNavigate();
  const [state, setState] = useState('loading'); // loading | ready | unauth | error
  const [data, setData] = useState({ upcoming: [], past: [] });

  const load = useCallback(async () => {
    try {
      const { data: resp } = await axios.get(`${baseUrl}/api/members/${tenant}/my-tickets`, {
        headers: memberHeaders(),
        withCredentials: true,
      });
      setData({ upcoming: resp.upcoming || [], past: resp.past || [] });
      setState('ready');
    } catch (err) {
      setState(err?.response?.status === 401 ? 'unauth' : 'error');
    }
  }, [tenant]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="min-h-screen bg-white dark:bg-neutral-950 text-black dark:text-white">
      <div className="max-w-2xl mx-auto p-6">
        <h1 className="text-2xl font-semibold mb-4">My tickets</h1>

        {state === 'loading' && <p className="opacity-70">Loading…</p>}
        {state === 'unauth' && (
          <div className="text-sm">
            <p className="mb-3">Please sign in to see your tickets.</p>
            <button onClick={() => navigate(`/${tenant}/memberLogin`)} className="px-3 py-2 rounded bg-cobalt text-white">
              Member sign in
            </button>
          </div>
        )}
        {state === 'error' && <p className="text-sm text-red-600">Couldn't load your tickets. Try again shortly.</p>}

        {state === 'ready' && (
          <>
            <Section title="Upcoming" orders={data.upcoming} empty="No upcoming events." />
            <Section title="Past" orders={data.past} empty="Nothing here yet." />
          </>
        )}
      </div>
    </div>
  );
}

function Section({ title, orders, empty }) {
  return (
    <section className="mb-6">
      <h2 className="text-sm uppercase tracking-wide opacity-60 mb-2">{title}</h2>
      {orders.length === 0 ? (
        <p className="text-sm opacity-60">{empty}</p>
      ) : (
        <div className="space-y-2">
          {orders.map((o) => (
            <div key={o.id} className="rounded-lg border border-black/10 dark:border-white/10 p-3">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="font-medium">{o.event.name}</div>
                  <div className="text-xs opacity-60">{formatDateRange(o.event.startdate, o.event.enddate)}</div>
                  <div className="text-xs opacity-70 mt-1">
                    {o.numTickets} × {o.tierName}
                    {o.status === 'refunded' && ' · refunded'}
                  </div>
                </div>
                {o.manageUrl && (
                  <a href={o.manageUrl} className="text-sm px-3 py-1.5 rounded border border-black/15 dark:border-white/15 whitespace-nowrap">
                    View / PDF
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
